import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";

import { ContentHeader, theme } from "@bxtech/bx-ui-lib";
import Button from "@bxtech/bx-ui-lib/elements/Button";
import Divider from "@bxtech/bx-ui-lib/elements/Divider";
import Link from "@bxtech/bx-ui-lib/elements/Link";
import TextFieldOld from "@bxtech/bx-ui-lib/elements/TextFieldOld";
import GoogleIcon from "@bxtech/bx-ui-lib/styles/primitives/icons/GoogleIcon";
// import MicrosoftIcon from "@bxtech/bx-ui-lib/styles/primitives/icons/MicrosoftIcon";
import { ChevronRightIcon } from "@bxtech/bx-ui-lib/styles/primitives/icons/MuiIcons";

import {
  DividerContainer,
  ErrorContainer,
  FormContainer,
  GetInTouchContainer,
  HeaderContainer,
  LogInAndBackButton,
  NextContainer,
  SSOButtons,
} from "./styles";

type Props = {
  stage: "email" | "password";
  setStage: (stage: "email" | "password") => void;
  errorMessage?: string;
  setErrorMessage: (errorMessage: string) => void;
  // handleMicrosoftSignIn: () => void;
  handleGoogleSignIn: () => void;
  onSubmit: (values: LoginFormValues) => void;
  forgottenPasswordOnClick?: () => void;
  style?: React.CSSProperties;
};

type LoginFormValues = {
  email: string;
  password: string;
};

const LogIn = ({
  stage,
  setStage,
  errorMessage,
  setErrorMessage,
  // handleMicrosoftSignIn,
  handleGoogleSignIn,
  onSubmit,
  forgottenPasswordOnClick,
  style,
}: Props) => {
  const { t } = useTranslation();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === "password" && passwordInputRef.current) {
      passwordInputRef.current?.focus();
    }
  }, [stage]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("common.validation.invalidEmail"))
      .required(t("common.validation.required")),
    password: Yup.string().required(t("common.validation.required")),
  });

  const initialValues: LoginFormValues = { email: "", password: "" };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values);
      setSubmitting(false);
    },
  });

  const handleNextClick = () => {
    if (stage === "email") {
      // Validate the email field and move to the password stage if there are no errors
      formik.validateField("email").then(() => {
        if (!formik.errors.email) {
          setStage("password");
        }
      });
    } else {
      // Submit the form if on the password stage
      formik.submitForm();
    }
  };

  return (
    <FormikProvider value={formik}>
      <FormContainer style={style}>
        <HeaderContainer>
          <ContentHeader
            heading={
              stage === "email"
                ? t("auth.login.title")
                : t("auth.login.enterPassword")
            }
            size="medium"
          />
        </HeaderContainer>
        {stage === "email" && (
          <>
            <>
              <SSOButtons>
                {/* <Button
                  label="Continue with Microsoft"
                  color="secondary"
                  variant="contained"
                  sx={{ height: "44px" }}
                  icon={<MicrosoftIcon />}
                  onClick={() => {
                    handleMicrosoftSignIn();
                  }}
                /> */}
                <Button
                  label={t("auth.login.continueWithGoogle")}
                  color="secondary"
                  variant="contained"
                  sx={{ height: "44px" }}
                  icon={<GoogleIcon />}
                  onClick={() => {
                    handleGoogleSignIn();
                  }}
                />
                {errorMessage === t("auth.login.errors.notAssociated") && (
                  <ErrorContainer>
                    <Typography variant="annotation" color="error">
                      {errorMessage}
                    </Typography>
                  </ErrorContainer>
                )}
              </SSOButtons>

              <DividerContainer>
                <Divider style={{ height: "1px", width: "25%" }} />
                <Typography
                  variant="subtitle2"
                  style={{ minWidth: "fit-content" }}
                >
                  {t("auth.login.orLoginViaEmail")}
                </Typography>
                <Divider style={{ height: "1px", width: "25%" }} />
              </DividerContainer>
            </>

            <TextFieldOld
              label={t("auth.login.emailAddress")}
              required
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              value={formik.values.email}
              onChange={(e) => {
                formik.handleChange(e);
                if (errorMessage) {
                  // Set the error message to an empty string if the user starts typing in the email field which will re enable the Next/LogIn Button
                  setErrorMessage("");
                }
              }}
              onBlur={formik.handleBlur}
              name="email"
              data-testid="email-input"
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  stage === "email" &&
                  !formik.errors.email
                ) {
                  setStage("password");
                }
              }}
              size="medium"
            />
          </>
        )}
        {stage === "password" && (
          <TextFieldOld
            label={t("auth.login.password")}
            required
            error={
              !!errorMessage ||
              (formik.touched.password && Boolean(formik.errors.password))
            }
            helperText={
              errorMessage ||
              (formik.touched.password && formik.errors.password)
            }
            value={formik.values.password}
            onChange={(e) => {
              formik.handleChange(e);
              if (errorMessage) {
                // Set the error message to an empty string if the user starts typing in the password field which will re enable the Next/LogIn Button
                setErrorMessage("");
              }
            }}
            onBlur={formik.handleBlur}
            name="password"
            data-testid="password-input"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                formik.handleSubmit();
              }
            }}
            size="medium"
            type="password"
            inputRef={passwordInputRef}
          />
        )}
        <NextContainer>
          <LogInAndBackButton>
            <Button
              label={
                stage === "email"
                  ? t("common.buttons.next")
                  : t("common.buttons.login")
              }
              sx={{
                width: "100%",
                height: "44px",
              }}
              color="primary"
              variant="contained"
              disabled={
                !!errorMessage ||
                (stage === "email" &&
                  (!!formik.errors.email || !formik.values.email)) ||
                (stage === "password" &&
                  (!!formik.errors.password || !formik.values.password))
              }
              onClick={() => {
                handleNextClick();
                setErrorMessage("");
              }}
            />
          </LogInAndBackButton>

          {stage === "email" ? (
            <>
              <GetInTouchContainer>
                <Typography variant="body2" color="textSecondary">
                  {t("auth.login.noAccount")}
                </Typography>
                <Link
                  label={t("auth.login.getInTouch")}
                  visited={false}
                  href="https://knowledge.bx.tech/farmer-contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<ChevronRightIcon />}
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
              </GetInTouchContainer>
              <GetInTouchContainer>
                <Typography variant="body2" color="textSecondary">
                  {t("auth.login.termsPrefix")}
                </Typography>
                <Link
                  label={t("auth.login.termsLink")}
                  visited={false}
                  href="https://knowledge.bx.tech/app-privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "underline",
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.text.primary,
                    },
                    display: "inline",
                  }}
                />
              </GetInTouchContainer>
            </>
          ) : (
            <Button
              label={t("auth.login.forgottenPassword")}
              sx={{
                height: "44px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
              }}
              color="primary"
              variant="text"
              onClick={forgottenPasswordOnClick}
            />
          )}
        </NextContainer>
      </FormContainer>
    </FormikProvider>
  );
};

export default LogIn;
